/**
 * ReviewQueueControls — the queue's two view switches (BON-381).
 *
 * Desktop gets the Show-resolved checkbox (off by default, so the page reads as
 * a to-do list); the phone gets an Open / Resolved tab pair instead, because
 * stacking a resolved block under two card lists puts the thing an operator
 * came for below the fold.
 */
import type { KeyboardEvent } from 'react';

import { REVIEW_CATEGORY_OPTIONS } from './reviewTypes';
import type { ReviewCategory } from './reviewTypes';

export interface ShowResolvedToggleProps {
  checked: boolean;
  resolvedCount: number;
  onChange: (next: boolean) => void;
}

export function ShowResolvedToggle({ checked, resolvedCount, onChange }: ShowResolvedToggleProps) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: 'var(--nb-muted)',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--nb-primary)', width: 14, height: 14 }}
      />
      Show resolved{' '}
      {/* U6: this used the API's CROSS-KIND `resolvedCount` while each section
          counted its own array length, so the number on the toggle and the rows it
          revealed were counting different populations — and under the default
          filter the resolved rows are not even loaded yet. The count now comes from
          the caller, which knows which it is quoting. */}
      <span style={{ color: 'var(--nb-faint)' }}>({resolvedCount})</span>
    </label>
  );
}

export interface CategoryFilterProps {
  active: ReviewCategory | null;
  onChange: (next: ReviewCategory | null) => void;
}

/**
 * Filter by KIND of problem.
 *
 * "All" first and selected by default, deliberately: a work list that hides work
 * unless you ask is how a backlog goes unnoticed, which is the exact failure this
 * whole queue exists to prevent.
 *
 * Chips rather than a `<select>` because there are five and the operator is
 * choosing what kind of work to do next, not filling in a form — and a chip row
 * shows every option at once, so nobody has to discover that a filter exists.
 *
 * Deliberately shows NO counts. A per-category count would need its own query
 * (the API's counts are cross-kind totals by design), and a count that lags the
 * rows is worse than none on a page whose whole job is to be trusted.
 */
export function ReviewCategoryFilter({ active, onChange }: CategoryFilterProps) {
  const chip = (value: ReviewCategory | null, label: string) => {
    const on = active === value;
    return (
      <button
        key={label}
        type="button"
        aria-pressed={on}
        onClick={() => onChange(value)}
        style={{
          height: 26,
          padding: '0 11px',
          borderRadius: 999,
          fontSize: 11.5,
          fontWeight: 700,
          cursor: 'pointer',
          border: `1px solid ${on ? 'transparent' : 'var(--nb-border)'}`,
          background: on ? 'var(--nb-primary)' : 'var(--nb-bg)',
          // The same per-theme token the buttons use: white on dark mode's mint
          // fill is 1.91:1.
          color: on ? 'var(--nb-on-primary)' : 'var(--nb-muted)',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      role="group"
      aria-label="Filter by problem type"
      style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}
    >
      {chip(null, 'All')}
      {REVIEW_CATEGORY_OPTIONS.map((o) => chip(o.value, o.label))}
    </div>
  );
}

export interface ReviewQueueTabsProps {
  active: 'open' | 'resolved';
  openCount: number;
  resolvedCount: number;
  onChange: (next: 'open' | 'resolved') => void;
  /** The id of the element these tabs control, for `aria-controls`. */
  panelId: string;
}

const TAB_ORDER: ('open' | 'resolved')[] = ['open', 'resolved'];

/**
 * The phone's Open / Resolved switch, as a REAL tablist.
 *
 * `role="tab"` is a promise about keyboard behaviour, and the first cut made the
 * promise without keeping it: no `aria-controls`, so a screen-reader user was told
 * "tab" with no way to know what it governed, and no arrow keys, so the pattern
 * every assistive-tech user expects simply did not work. A roving `tabIndex` is
 * the other half — a tablist is ONE tab stop, and the arrows move within it.
 */
export function ReviewQueueTabs({
  active,
  openCount,
  resolvedCount,
  onChange,
  panelId,
}: ReviewQueueTabsProps) {
  const onKeyDown = (e: KeyboardEvent) => {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next =
      TAB_ORDER[(TAB_ORDER.indexOf(active) + delta + TAB_ORDER.length) % TAB_ORDER.length];
    onChange(next);
  };

  const tab = (id: 'open' | 'resolved', label: string, n: number) => {
    const on = active === id;
    return (
      <button
        key={id}
        type="button"
        role="tab"
        id={`${panelId}-tab-${id}`}
        aria-selected={on}
        aria-controls={panelId}
        // Roving tabindex: the tablist is one stop, the arrows move inside it.
        tabIndex={on ? 0 : -1}
        onClick={() => onChange(id)}
        onKeyDown={onKeyDown}
        style={{
          flex: 1,
          height: 38,
          borderRadius: 999,
          border: `1px solid ${on ? 'transparent' : 'var(--nb-border)'}`,
          background: on ? 'var(--nb-primary)' : 'var(--nb-bg)',
          // The same 1.91:1 dark-mode failure the primary BUTTON had, in an inline
          // style where the CSS token fix could not reach it.
          color: on ? 'var(--nb-on-primary)' : 'var(--nb-muted)',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {label} <span style={{ opacity: 0.75 }}>{n}</span>
      </button>
    );
  };

  return (
    <div role="tablist" aria-label="Review state" style={{ display: 'flex', gap: 8 }}>
      {tab('open', 'Open', openCount)}
      {tab('resolved', 'Resolved', resolvedCount)}
    </div>
  );
}
